import type {
  ToolCallMessagePartComponent,
  ToolCallMessagePartStatus,
} from '@assistant-ui/react';
import {type AutoColor, Icon, type IconName} from '@wandb/components';
import React, {memo, useContext} from 'react';
import {
  Button,
  Disclosure,
  DisclosurePanel,
  DisclosureStateContext,
  Heading,
} from 'react-aria-components';

import {CodeSnippet} from '../CodeSnippet';
import {ErrorBoundary} from '../ErrorBoundary';
import {useNightModeRevertFilter} from './useNightModeRevertFilter';
import {extractPythonCodeBlock, isShellResult} from './utils';

const statusConfig: Record<
  ToolCallMessagePartStatus['type'] | 'error',
  {icon: IconName; color: AutoColor}
> = {
  running: {icon: 'loading', color: 'moon'},
  complete: {icon: 'checkmark-circle', color: 'green'},
  incomplete: {icon: 'failed-error-deleted', color: 'red'},
  error: {icon: 'failed-error-deleted', color: 'red'},
  'requires-action': {icon: 'warning', color: 'sienna'},
};

const ToolCallTrigger: React.FC<{
  toolName: string;
  status: ToolCallMessagePartStatus;
  isError?: boolean;
}> = ({toolName, status, isError}) => {
  const statusType = isError ? 'error' : status.type;
  const config = statusConfig[statusType];
  return (
    <Heading className="m-0">
      <Button
        slot="trigger"
        className="relative flex w-full gap-8 space-between items-center self-stretch bg-transparent px-12 cursor-pointer text-moon-800">
        <span
          className={
            config.icon === 'loading'
              ? 'inline-flex animate-spin [animation-duration:4s]'
              : 'inline-flex'
          }>
          <Icon icon={config.icon} color={config.color} />
        </span>
        <span className="grow text-left leading-[21px]">{toolName}</span>
        <span className="inline-flex rotate-0 transition-[rotate] duration-[220ms] group-data-[expanded]/tool-call:rotate-90">
          <Icon icon="chevron-next" size="sm" />
        </span>
      </Button>
    </Heading>
  );
};

const ToolCallCodeSnippet = ({
  codeSnippet,
  language = 'json',
  title,
}: {
  codeSnippet: string;
  language?: string;
  title?: string;
}) => {
  let formattedCode;
  try {
    formattedCode = JSON.stringify(JSON.parse(codeSnippet), null, 2);
  } catch {
    formattedCode = codeSnippet;
  }
  return (
    <div>
      {title && (
        <div className="py-4 font-normal text-sm font-[Inconsolata] text-moon-500">
          {title}
        </div>
      )}
      <CodeSnippet
        showExpandCollapse={true}
        codeSnippet={formattedCode}
        codeLanguage={language}
      />
    </div>
  );
};

const ToolCallArgs: React.FC<{argsText?: string}> = ({argsText}) => {
  if (!argsText) {
    return null;
  }
  let parsed: Record<string, unknown> | null = null;
  try {
    parsed = JSON.parse(argsText);
  } catch {
    // not valid JSON — fall through to generic display
  }

  const command =
    parsed && typeof parsed.command === 'string' ? parsed.command : null;
  const pythonCodeBlock = command ? extractPythonCodeBlock(command) : null;

  const diff = parsed && typeof parsed.diff === 'string' ? parsed.diff : null;
  const diffLang =
    parsed?.path && String(parsed.path).endsWith('.py') ? 'python' : 'text';

  const snippet = pythonCodeBlock
    ? {code: pythonCodeBlock, language: 'python', title: 'python'}
    : command
      ? {code: command, language: 'bash', title: 'bash'}
      : diff
        ? {
            code: diff,
            language: diffLang,
            title: `${parsed?.operation ?? 'patch'}: ${parsed?.path ?? ''}`,
          }
        : {code: argsText, language: 'json', title: undefined};

  return (
    <div className="px-12">
      <p className="font-normal text-sm font-[Inconsolata] pb-4 text-moon-500">
        ARGS
      </p>
      <ToolCallCodeSnippet
        codeSnippet={snippet.code}
        language={snippet.language}
        title={snippet.title}
      />
      {parsed?.timeout_ms != null && (
        <div className="font-normal text-sm font-[Inconsolata] pb-4 text-moon-500">
          Timeout: {String(parsed.timeout_ms)}
        </div>
      )}
    </div>
  );
};

const ToolCallResult: React.FC<{result?: unknown}> = ({result}) => {
  const resultStr = typeof result === 'string' ? result : undefined;
  if (resultStr === undefined) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(resultStr);
  } catch {
    parsed = null;
  }

  let resultBody: React.JSX.Element;
  if (isShellResult(parsed)) {
    const {stdout = '', stderr = '', exit_code} = parsed;
    resultBody = (
      <div className="gap-4">
        {stdout && (
          <ToolCallCodeSnippet codeSnippet={stdout} title={'stdout'} />
        )}
        {stderr && (
          <ToolCallCodeSnippet codeSnippet={stderr} title={'stderr'} />
        )}
        {exit_code !== undefined && (
          <div className="font-normal text-sm font-[Inconsolata] pb-4 text-moon-500">
            Exit code: {exit_code}
          </div>
        )}
      </div>
    );
  } else {
    resultBody = <ToolCallCodeSnippet codeSnippet={resultStr} />;
  }
  return (
    <div className="border-t border-dashed border-moon-250 px-12 pt-8">
      <p className="font-normal text-sm font-[Inconsolata] pb-4 text-moon-500">
        OUTPUT
      </p>
      {resultBody}
    </div>
  );
};

const ToolCallError: React.FC<{
  status: ToolCallMessagePartStatus;
}> = ({status}) => {
  if (status.type !== 'incomplete') {
    return null;
  }
  const error = status.error;
  const errorText = error
    ? typeof error === 'string'
      ? error
      : JSON.stringify(error)
    : null;
  if (!errorText) {
    return null;
  }
  return (
    <div className="px-12">
      <p className="font-semibold text-xs text-moon-500">
        {status.reason === 'cancelled' ? 'Cancelled reason:' : 'Error:'}
      </p>
      <p className="text-moon-500">{errorText}</p>
    </div>
  );
};

const LazyDisclosureContent: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const state = useContext(DisclosureStateContext);
  if (!state?.isExpanded) {
    return null;
  }
  return (
    <div className="relative mt-8 flex flex-col gap-8 border-t border-moon-250 pt-8">
      {children}
    </div>
  );
};

const ToolCallImpl: ToolCallMessagePartComponent = props => (
  <ErrorBoundary
    silent
    onError={error => {
      console.error('[WBAgentToolCall] render crash', error);
    }}
    renderError={() => (
      <div className="rounded-lg border border-red-300 bg-red-50 p-8 text-sm text-red-700">
        <div className="font-semibold">Tool call failed to render</div>
        <div className="mt-4 text-xs">
          Something went wrong rendering this tool call. The error has been
          logged.
        </div>
      </div>
    )}>
    <ToolCallImplInner {...props} />
  </ErrorBoundary>
);

const ToolCallImplInner: ToolCallMessagePartComponent = ({
  toolName,
  argsText,
  result,
  status,
  isError,
}: {
  toolName: string;
  argsText?: string;
  result?: unknown;
  status: ToolCallMessagePartStatus;
  isError?: boolean;
}) => {
  const isCancelled =
    status.type === 'incomplete' && status.reason === 'cancelled';
  const nightModeRevertFilter = useNightModeRevertFilter();

  return (
    <Disclosure defaultExpanded={false} className="group/tool-call">
      <div
        className={`relative w-full col-auto gap-8 rounded-lg border border-moon-250 py-8 ${
          isCancelled ? 'opacity-60' : ''
        }`}>
        <div
          className={'absolute inset-0 rounded-lg dark:bg-moon-900'}
          style={{filter: nightModeRevertFilter}}
        />
        <ToolCallTrigger
          toolName={toolName}
          status={status}
          isError={isError}
        />
        <DisclosurePanel className="relative h-[--disclosure-panel-height] overflow-clip transition-[height] duration-200">
          <LazyDisclosureContent>
            <ToolCallError status={status} />
            <ToolCallArgs argsText={argsText} />
            {!isCancelled && <ToolCallResult result={result} />}
          </LazyDisclosureContent>
        </DisclosurePanel>
      </div>
    </Disclosure>
  );
};

export const WBAgentToolCall = memo(
  ToolCallImpl
) as ToolCallMessagePartComponent;
WBAgentToolCall.displayName = 'WBAgentToolCall';
